import React, { useEffect, useState } from 'react'
import { FormProvider, useController, useForm, useFormState } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { CompletionStatus, SectionName } from '_gql'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import { TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import TilaErrorMessage from '@sputnik/components/TilaErrorMessage'
import VerifyPhoneNumberModal, { useVerifyPhoneNumber } from '@sputnik/components/VerifyPhoneNumber'
import useGetCreditAppTypes from '@sputnik/hooks/useGetCreditAppTypes'
import useGetInfoForApplicationAgreements from '@sputnik/hooks/useGetInfoForApplicationAgreements'
import CreditApplicationPageLayout from '@sputnik/pages/CreditApplicationPages/CreditApplicationPageLayout'
import ApplicationAgreements from '@sputnik/pages/PostApplicationPages/ApplicationAgreements'
import { useGetApplicationDecisions } from '@sputnik/pages/PostApplicationPages/CreditLineOffersPage/GetApplicationDecisions.gql'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'
import { getClientTimezone } from '@sputnik/utils/timezoneUtil'

import AuthorizedSignerReviewSection from './AuthorizedSignerReviewSection'
import BankAccountReviewSection from './BankAccountReviewSection'
import BusinessAddressReviewSection from './BusinessAddressReviewSection'
import CompanyOwnersReviewSection from './CompanyOwnersReviewSection'
import FCRATerms from './FCRATerms'
import FinancialInfoReviewSection from './FinancialInfoReviewSection'
import {
  useGetCreditApplicationForReview,
  useGetCreditApplicationForReviewLazyQuery,
} from './GetCreditApplicationForReview.gql'
import NameAndOwnershipReviewSection from './NameAndOwnershipReviewSection'
import OutsourcedAccountantReviewSection from './OutsourcedAccountantReviewSection'
import ReviewFormMarketingContent from './ReviewFormMarketingContent'
import { type ReviewFormSchema } from './reviewFormSchema'
import { useSendApplicationToSigner } from './SendApplicationToSigner.gql'
import styles from './styles.module.scss'
import { useSubmitCreditApplication } from './SubmitCreditApplication.gql'
import { useSubmitDataVerification } from './SubmitDataVerification.gql'
import { useSubmitMajorAccountChange } from './SubmitMajorAccountChange.gql'
import { formattedErrorMessage } from './utils'

const ReviewForm = () => {
  const navigate = useNavigate()
  const { appId } = useParams()
  const [getClassName] = useNameFormatter('ReviewForm')
  const showToast = useToast()
  const { isMajorAccountChange, isMigration, isLoadingCreditAppTypes } = useGetCreditAppTypes({
    salesforceCreditId: appId ?? '',
  })
  const isMacOrMigration = isMajorAccountChange || isMigration

  const { tracker } = useWebTracking()

  useEffect(() => {
    tracker?.track('se_signup__review_and_sign__presented')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [isVerifyingPhoneNumber, setIsVerifyingPhoneNumber] = useState(false)
  const { doesPhoneNumberNeedVerified } = useVerifyPhoneNumber({ salesforceCreditId: appId ?? '' })

  const { data } = useGetCreditApplicationForReview({
    fetchPolicy: 'network-only',
    variables: { creditApplicationId: appId ?? '' },
    skip: isLoadingCreditAppTypes,
  })
  const [getCreditApplicationForReview] = useGetCreditApplicationForReviewLazyQuery({
    variables: { creditApplicationId: appId ?? '' },
  })

  const [sendApplicationToSigner, { loading: isSendingToSigner }] = useSendApplicationToSigner({
    variables: { creditApplicationId: appId ?? '' },
    onCompleted: () => {
      logInfo('sendApplicationToSigner', {
        attributes: {
          creditAppId: appId,
          message: 'Successfully sent the application to signer',
        },
        eventName: 'sendApplicationToSignerSuccess',
      })

      showToast(
        TOAST_TYPE_SUCCESS,
        <FormattedMessage
          defaultMessage='Your application has been sent to the signer.'
          id='sputnik.ReviewForm__1lme1Q'
        />,
      )
      navigate(`../${PATHNAME_CONSTS.DASHBOARD_PATH}`)
    },
    onError: (error) => {
      logError(error, {
        attributes: {
          creditAppId: appId,
          action: 'sendApplicationToSigner',
          message: error?.message,
          result: 'Did not send application to signer',
        },
        eventName: 'sendApplicationToSignerFailure',
      })

      tracker?.track('se_signup__error_message__fired', {
        error_field: 'sendApplicationToSigner',
        error_message_text: error.message,
      })

      showToast(
        TOAST_TYPE_DANGER,
        error?.message ? (
          <FormattedMessage
            defaultMessage='Error sending to signer: {errorMessage}. Fix error(s) and try again.'
            id='sputnik.ReviewForm__ahkYwd'
            values={{
              errorMessage: formattedErrorMessage(error?.message),
            }}
          />
        ) : (
          <FormattedMessage
            defaultMessage='There was an error sending the application to the signer. Please try again.'
            id='sputnik.ReviewForm__xBBkLl'
          />
        ),
        { className: 'fs-unmask' },
      )
    },
  })

  const [submitCreditApplication, { loading: isSumbittingCreditApp }] = useSubmitCreditApplication({
    onCompleted: () => {
      logInfo('submitCreditApplication', {
        attributes: {
          creditAppId: appId,
          message: 'Successfully submitted the credit application',
        },
        eventName: 'submitCreditApplicationSuccess',
      })

      tracker?.track('se_signup__has__completed')

      showToast(
        TOAST_TYPE_SUCCESS,
        <FormattedMessage
          defaultMessage='Your credit application has been successfully submitted.'
          id='sputnik.ReviewForm__dr9M3/'
        />,
      )
      navigate(`../${PATHNAME_CONSTS.SUBMITTING_APPLICATION}`)
    },
    onError: (error) => {
      logError(error, {
        attributes: {
          creditAppId: appId,
          action: 'submitCreditApplication',
          message: error?.message,
          result: 'Did not submit credit application',
        },
        eventName: 'submitCreditApplicationFailure',
      })

      tracker?.track('se_signup__error_message__fired', {
        error_field: 'submitCreditApplication',
        error_message_text: error.message,
      })
      showToast(
        TOAST_TYPE_DANGER,
        error?.message ? (
          <FormattedMessage
            defaultMessage='Error on submission: {errorMessage}. Fix error(s) and try again.'
            id='sputnik.ReviewForm__h4qnEp'
            values={{
              errorMessage: formattedErrorMessage(error?.message),
            }}
          />
        ) : (
          <FormattedMessage
            defaultMessage='There was an error submitting your credit application. Please try again.'
            id='sputnik.ReviewForm__aKBx57'
          />
        ),
        { className: 'fs-unmask' },
      )
    },
  })

  const [submitMajorAccountChange, { loading: isSumbittingMajorAccountChange }] = useSubmitMajorAccountChange({
    onCompleted: () => {
      logInfo('submitMajorAccountChange', {
        attributes: {
          creditAppId: appId,
          message: 'Successfully submitted major account change',
        },
        eventName: 'submitMajorAccountChangeSuccess',
      })

      showToast(
        TOAST_TYPE_SUCCESS,
        <FormattedMessage
          defaultMessage='Your credit application changes have been successfully submitted.'
          id='sputnik.ReviewForm__Kj6Wqj'
        />,
      )
      navigate(`../${PATHNAME_CONSTS.SUBMITTING_APPLICATION}`)
    },
    onError: (error) => {
      logError(error, {
        attributes: {
          creditAppId: appId,
          action: 'submitMajorAccountChange',
          message: error?.message,
          result: 'Did not submit major account change',
        },
        eventName: 'submitMajorAccountChangeFailure',
      })

      tracker?.track('se_signup__error_message__fired', {
        error_field: 'submitMajorAccountChange',
        error_message_text: error.message,
      })

      showToast(
        TOAST_TYPE_DANGER,
        error?.message ? (
          <FormattedMessage
            defaultMessage='Error on submission: {errorMessage}. Fix error(s) and try again.'
            id='sputnik.ReviewForm__h4qnEp'
            values={{
              errorMessage: formattedErrorMessage(error?.message),
            }}
          />
        ) : (
          <FormattedMessage
            defaultMessage='There was an error submitting your credit application changes. Please try again.'
            id='sputnik.ReviewForm__wRDXSw'
          />
        ),
        { className: 'fs-unmask' },
      )
    },
  })

  const [submitDataVerification, { loading: isSubmittingDataVerification }] = useSubmitDataVerification({
    onCompleted: () => {
      logInfo('submitDataVerification', {
        attributes: {
          creditAppId: appId,
          message: 'Successfully submitted migration',
        },
        eventName: 'submitDataVerificationSuccess',
      })

      showToast(
        TOAST_TYPE_SUCCESS,
        <FormattedMessage
          defaultMessage='Your credit application has been submitted for verification.'
          id='sputnik.ReviewForm__wyiOw3'
        />,
      )
      navigate(`../${PATHNAME_CONSTS.SUBMITTING_APPLICATION}`)
    },
    onError: (error) => {
      logError(error, {
        attributes: {
          creditAppId: appId,
          action: 'submitDataVerification',
          message: error?.message,
          result: 'Did not submit migration',
        },
        eventName: 'submitDataVerificationFailure',
      })

      tracker?.track('se_signup__error_message__fired', {
        error_field: 'submitDataVerification',
        error_message_text: error.message,
      })

      showToast(
        TOAST_TYPE_DANGER,
        error?.message ? (
          <FormattedMessage
            defaultMessage='Error on submission: {errorMessage}. Fix error(s) and try again.'
            id='sputnik.ReviewForm__h4qnEp'
            values={{
              errorMessage: formattedErrorMessage(error?.message),
            }}
          />
        ) : (
          <FormattedMessage
            defaultMessage='There was an error submitting your credit application verification. Please try again.'
            id='sputnik.ReviewForm__b9bYMY'
          />
        ),
        { className: 'fs-unmask' },
      )
    },
  })

  const methods = useForm<ReviewFormSchema>({
    mode: 'all',
    defaultValues: {
      creditReportConsent: false,
    },
  })

  const { handleSubmit, control } = methods
  const { isLoading } = useFormState({ control })
  const {
    field: { value: creditReportConsent, onChange: onCreditReportConsentChange },
  } = useController({ control, name: 'creditReportConsent' })

  const companyDivvyUuid = data?.creditApplication?.companyDivvyUuid
  const { data: decisionsData } = useGetApplicationDecisions({
    variables: { companyId: companyDivvyUuid || '' },
    skip: !isMigration || !companyDivvyUuid, // only need decisions for migrations
  })

  const {
    creditApplication: creditApplicationForTilaComponent,
    hasAgreedToTila,
    isLoggedInUserAuthorizedSigner,
    tilaInfoError,
  } = useGetInfoForApplicationAgreements({
    // no decisions will avoid calling for tila info
    decision: decisionsData?.decisions?.[0],
    hasAgreedToTerms: creditReportConsent,
    isLoadingAcceptingOffer: false,
  })

  const { underwritingDecisionUuid, underwritingOfferType } = creditApplicationForTilaComponent?.tila?.meta || {}

  const areAllSectionsComplete = data?.creditApplication?.savedSectionsInfo
    ?.filter(
      (section) =>
        section?.sectionName === SectionName.PEOPLE_INFO ||
        (!isMacOrMigration && section?.sectionName === SectionName.FINANCE_INFO) ||
        section?.sectionName === SectionName.BUSINESS_INFO,
    )
    .every((section) => section?.completionStatus === CompletionStatus.COMPLETED)

  const isSubmitting =
    isSumbittingCreditApp || isSendingToSigner || isSumbittingMajorAccountChange || isSubmittingDataVerification

  const canSubmitApplication = creditReportConsent && areAllSectionsComplete && !isSubmitting
  const canSendToSigner = areAllSectionsComplete && !isSendingToSigner
  const enableReviewButton = isLoggedInUserAuthorizedSigner ? canSubmitApplication : canSendToSigner

  const showTilaAgreement =
    isLoggedInUserAuthorizedSigner && !isLoading && isMigration && creditApplicationForTilaComponent && !tilaInfoError

  const onSubmit = async () => {
    if (!isLoggedInUserAuthorizedSigner) {
      await sendApplicationToSigner()
      return
    }
    const { data } = await getCreditApplicationForReview()
    const { verifiedPhoneNumber, email, phoneNumber } = data?.creditApplication?.authorizedSigner ?? {}

    if (
      // If they have gotten here, that means they are the authorized signer
      // so we do not need to recheck that
      doesPhoneNumberNeedVerified({
        verifiedPhoneNumber: verifiedPhoneNumber ?? '',
        authSignerEmail: email ?? '',
        phoneNumber: phoneNumber ?? '',
      })
    ) {
      setIsVerifyingPhoneNumber(true)
    } else {
      let fraudPreventionData = ''
      try {
        const blackBoxInfo =
          typeof window.ioGetBlackbox === 'function'
            ? window.ioGetBlackbox
            : typeof window.IGLOO?.getBlackbox === 'function'
            ? window.IGLOO.getBlackbox
            : null
        fraudPreventionData = blackBoxInfo ? blackBoxInfo().blackbox || '' : ''
      } catch {
        fraudPreventionData = ''
      }

      if (isMajorAccountChange) {
        await submitMajorAccountChange({
          variables: {
            creditApplicationId: appId ?? '',
            creditTerms: creditReportConsent,
            creditApplicationFetchedAt: data?.creditApplication?.fetchedAt,
          },
        })
      } else if (isMigration) {
        await submitDataVerification({
          variables: {
            creditApplicationId: appId ?? '',
            creditTerms: creditReportConsent,
            creditApplicationFetchedAt: data?.creditApplication?.fetchedAt,
            clientTimeZone: getClientTimezone(),
          },
        })
      } else {
        tracker?.track('se_signup__review_and_sign__submitted')
        await submitCreditApplication({
          variables: {
            creditApplicationId: appId ?? '',
            creditTerms: creditReportConsent,
            creditApplicationFetchedAt: data?.creditApplication?.fetchedAt,
            fraudPreventionData: fraudPreventionData,
            clientTimeZone: getClientTimezone(),
          },
        })
      }
    }
  }

  return (
    <div className={styles[getClassName('')]}>
      <FormProvider {...methods}>
        <CreditApplicationPageLayout
          isNextButtonDisabled={!enableReviewButton}
          isNextButtonLoading={isSubmitting}
          marketing={<ReviewFormMarketingContent />}
          nextButtonText={
            <FormattedMessage
              defaultMessage='{isLoggedInUserAuthorizedSigner, select,
                  true {Submit}
                  other {Send to signer}
                }'
              id='sputnik.CreditApplicationPageLayout__e2Z/Kx'
              values={{ isLoggedInUserAuthorizedSigner }}
            />
          }
          pageTitle={
            <FormattedMessage
              defaultMessage='Final review'
              id='sputnik.ReviewForm__Csk9Zg'
            />
          }
          onBackClick={() => navigate(`../${PATHNAME_CONSTS.PEOPLE_INFO_PATH}`)}
          onNextClick={handleSubmit(onSubmit)}
        >
          <NameAndOwnershipReviewSection />
          <BusinessAddressReviewSection />
          <FinancialInfoReviewSection />
          {!isMacOrMigration && <BankAccountReviewSection />}
          <AuthorizedSignerReviewSection />
          <CompanyOwnersReviewSection />
          <OutsourcedAccountantReviewSection />
          <div className={styles[getClassName('agreements')]}>
            {isLoggedInUserAuthorizedSigner && !isLoading && !isMigration && (
              <FCRATerms dsaVersion={data?.creditApplication?.dsaVersion || ''} />
            )}
            {showTilaAgreement && (
              <ApplicationAgreements
                // I want to refactor this component post launch to not pass the credit app
                creditApplication={creditApplicationForTilaComponent}
                decisionId={underwritingDecisionUuid}
                hasAgreedToTerms={creditReportConsent}
                hasAgreedToTila={hasAgreedToTila}
                offerType={underwritingOfferType}
                onCheckboxClick={() => {
                  onCreditReportConsentChange(!creditReportConsent)
                }}
              />
            )}
            {tilaInfoError && <TilaErrorMessage />}
          </div>
        </CreditApplicationPageLayout>
      </FormProvider>
      {isVerifyingPhoneNumber && (
        <VerifyPhoneNumberModal
          onClose={() => {
            setIsVerifyingPhoneNumber(false)
          }}
          onPhoneNumberVerified={onSubmit}
        />
      )}
    </div>
  )
}

export default ReviewForm
